import {
  getMagicBrushError,
  hasMagicBrushError,
  Image,
  ProcessingError,
  ProcessingState,
  ResultVariant,
} from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { useEditorStore } from "@/stores/editor_store";
import { getNestedKey } from "@/modules/utils";

export const usePoll = () => {
  const store = useEditorStore();
  const timeouts: Map<string, any> = new Map();

  const createTimeoutKey = (variant: ResultVariant, imageId: string): string => {
    return `${variant}.${imageId}.timeout`;
  };

  const isFinishedState = (state: ProcessingState): boolean => {
    return [ProcessingState.Finished, ProcessingState.Error].includes(state);
  };

  const clearTimeoutAndResolve = (timeoutKey: string, resolve: (value: Image) => void, image: Image) => {
    clearTimeout(timeouts.get(timeoutKey));
    timeouts.delete(timeoutKey);
    resolve(image);
  };

  const executePolling = async (
    image: Image,
    timeoutKey: string,
    getStateAndNextFetch: (updatedImage: Image) => { state: ProcessingState; nextFetchIn: number },
    handleError?: (updatedImage: Image) => boolean
  ): Promise<Image> => {
    const executePoll = async (
      image: Image,
      resolve: (value: Image) => void,
      reject: (reason?: any) => void
    ): Promise<void> => {
      const updatedImage: Image = await Client.getImage(image.meta.id);

      if (handleError?.(updatedImage)) {
        return;
      }

      store.updateImage(updatedImage);

      const { state, nextFetchIn } = getStateAndNextFetch(updatedImage);

      if (isFinishedState(state)) {
        clearTimeoutAndResolve(timeoutKey, resolve, updatedImage);
      } else {
        const timeout = setTimeout(() => {
          executePoll(updatedImage, resolve, reject);
        }, nextFetchIn);
        timeouts.set(timeoutKey, timeout);
      }
    };

    return new Promise((resolve, reject) => {
      executePoll(image, resolve, reject);
    });
  };

  const poll = async (
    image: Image,
    variant: ResultVariant,
    handleProcessingError: (processingError: ProcessingError) => void
  ): Promise<Image> => {
    const timeoutKey = createTimeoutKey(variant, image.meta.id);

    const getStateAndNextFetch = (updatedImage: Image) => {
      const isMagicBackgroundVariant = [ResultVariant.MagicBackgroundPreview].includes(variant);

      if (isMagicBackgroundVariant) {
        const generation = Array.from(updatedImage.magicBackgroundResults.entries()).pop()?.[1];
        return {
          state: generation?.[variant].state,
          nextFetchIn: generation?.[variant].nextFetchIn,
        };
      }

      return {
        state: getNestedKey(updatedImage, `${variant}.state`) as ProcessingState,
        nextFetchIn: getNestedKey(updatedImage, `${variant}.nextFetchIn`),
      };
    };

    const handleError = (updatedImage: Image): boolean => {
      const isMagicBrushVariant = [ResultVariant.MagicBrushPreview].includes(variant);

      if (isMagicBrushVariant && hasMagicBrushError(updatedImage)) {
        const processingError: ProcessingError = getMagicBrushError(updatedImage);
        store.clearLines();
        handleProcessingError(processingError);
        store.selectedImage.magicBrushPreviewResult.state = ProcessingState.Error;
        return true;
      }

      return false;
    };

    return executePolling(image, timeoutKey, getStateAndNextFetch, handleError);
  };

  const pollMagicBackgroundHD = async (image: Image, variant: ResultVariant, generationId: string): Promise<Image> => {
    const timeoutKey = createTimeoutKey(variant, image.meta.id);

    const getStateAndNextFetch = (updatedImage: Image) => {
      const generation = updatedImage.magicBackgroundResults.get(generationId);

      if (!generation) {
        throw new Error(`Generation ${generationId} not found`);
      }

      const magicBackgroundHdState = generation[ResultVariant.MagicBackgroundHd]?.state;
      const foregroundHdState = generation.foregroundHdResult?.state;

      const magicBackgroundHdNextFetch =
        "nextFetchIn" in generation[ResultVariant.MagicBackgroundHd]
          ? generation[ResultVariant.MagicBackgroundHd].nextFetchIn
          : 0;
      const foregroundHdNextFetch =
        "nextFetchIn" in generation.foregroundHdResult ? generation.foregroundHdResult.nextFetchIn : 0;

      const isMagicBackgroundHdFinished = isFinishedState(magicBackgroundHdState);
      const isForegroundHdFinished = isFinishedState(foregroundHdState);

      const state =
        isMagicBackgroundHdFinished && isForegroundHdFinished ? ProcessingState.Finished : ProcessingState.Processing;

      const nextFetchIn = Math.max(magicBackgroundHdNextFetch ?? 0, foregroundHdNextFetch ?? 0);

      return { state, nextFetchIn };
    };

    return executePolling(image, timeoutKey, getStateAndNextFetch);
  };

  return { poll, pollMagicBackgroundHD };
};
